import React, { useState, useEffect } from 'react';
import { Button, Upload, Space, Modal, Image, message, Tooltip, Form, Input, Alert, Steps, Spin, Row, Col } from 'antd';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import authService from '../utils/auth';

/**
 * 中国团附加证件（身份证 / 台胞证）的「补传 + 查看」单元格。
 *
 * 为什么抽成组件（2026-08-17）：这套逻辑原本只写在「游客管理」页里，销售端的「我的团」「我的游客」
 * 和运营的「出团管理」都看不到、也传不了，导致 85 位需要交证件的客人一张都没收上来。
 * 三处各抄一份必然走样，统一走这里。
 *
 * 展示规则（用户 2026-08-17 定）：**先有护照，才谈补证件**——
 *   · 本团不要求证件 / 护照国籍不是中国·台湾 → 显示 `-`（不需要交）
 *   · 还没传护照                          → 显示灰字「待传护照」（证件类型要靠护照国籍才能定）
 *   · 护照已传、证件未传                   → 显示「补传身份证 / 补传台胞证」按钮
 *   · 证件已传                            → 「查看 / 重传」两个文字链（不挂标签，列窄时会挤到隔壁列）
 *
 * 两种形态：mode='cell' 表格单元格；mode='block' 编辑弹窗里的「证件照片」整块（缩略图 + 上传按钮）。
 *
 * 上传流程与护照一致，分两步（用户 2026-08-17 要求）：
 *   ① 选图 → `POST /api/tourists/:id/update-china-doc-preview` 只识别不落库，回填表单并给出核对提示
 *      （证件姓名与护照对不上、出生日期不一致、证件类型与国籍不符等，只提示不挡路）
 *   ② 销售核对/修改后确认 → `POST /api/tourists/:id/update-china-doc`，带 confirmData 落库
 *   识别失败不阻断：可直接手工填姓名和号码提交，号码格式仍由后端校验（身份证 18 位带校验位）。
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

/**
 * 该客人要交哪种证件。与后端 utils/chinaDocRule.getChinaDocType 保持一致：
 * 本团要求 + 中国护照 → 身份证；台湾护照 → 台胞证；其余国籍不需要。
 * 证件传过之后 nationality 万一为空，用 chinaDocType 兜底，避免已传的反而显示不出标签。
 */
export function getChinaDocLabel(record) {
  if (!record?.chinaDocRequired) return null;
  const n = String(record.nationality || '').toUpperCase();
  if (n.startsWith('CHN') || n.startsWith('中国')) return '身份证';
  if (n.startsWith('TWN') || n.startsWith('台湾')) return '台胞证';
  if (record.chinaDocType === 'china_id') return '身份证';
  if (record.chinaDocType === 'taiwan_permit') return '台胞证';
  return null;
}

/** 护照是否已经交了（证件类型要靠护照国籍判定，所以必须先有护照） */
function hasPassport(record) {
  return !!(record?.passportPhoto || record?.passportNumber);
}

function photoUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API_BASE.replace('/api', '')}${path}`;
}

/**
 * @param {object} record   游客记录（需含 chinaDocRequired / nationality / passportPhoto / chinaDoc*）
 * @param {function} onDone 上传成功后的刷新回调（各页面传自己的 fetch 函数）
 * @param {boolean} [showViewer=true] 是否内置查看弹窗（护照弹窗里已经展示过证件的页面可传 false）
 * @param {'cell'|'block'} [mode='cell'] cell=表格单元格里的操作；block=编辑弹窗里的「证件照片」整块（带缩略图）
 */
export default function ChinaDocAction({ record, onDone, showViewer = true, mode = 'cell' }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);            // 选中的原图，两步都用同一张
  const [previewUrl, setPreviewUrl] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [ocrFailed, setOcrFailed] = useState(null);  // 识别失败原因（不挡路，可手填）
  // 刚上传成功的结果（后端直接返回，含可用的图片地址）。
  // 编辑弹窗里上传完不能靠刷新列表来更新缩略图 —— 那样得关掉弹窗，销售填了一半的表单就丢了。
  const [justUploaded, setJustUploaded] = useState(null);
  const [form] = Form.useForm();

  // 组件卸载时释放预览图，避免 blob 泄漏
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const label = getChinaDocLabel(record);
  // block 形态由调用方决定是否渲染，这里不占位；cell 形态用「-」占位保持列对齐
  if (!label) return mode === 'block' ? null : <span style={{ color: '#999' }}>-</span>;
  // 已传的证件：优先取本次上传结果，其次取列表里的记录
  const doc = {
    photo: justUploaded?.chinaDocPhoto || record.chinaDocPhoto,
    name: justUploaded?.chinaDocName ?? record.chinaDocName,
    number: justUploaded?.chinaDocNumber ?? record.chinaDocNumber,
    expiryDate: justUploaded?.chinaDocExpiryDate ?? record.chinaDocExpiryDate,
    uploadedAt: justUploaded ? new Date() : record.chinaDocUploadedAt,
  };
  const isIdCard = label === '身份证';

  const resetModal = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setWarnings([]);
    setOcrFailed(null);
    form.resetFields();
  };

  const closeUpload = () => { setUploadOpen(false); resetModal(); };

  // ① 选图即识别，回填表单
  const handleSelect = async (picked) => {
    resetModal();
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
    setRecognizing(true);
    try {
      const formData = new FormData();
      formData.append('chinaDoc', picked);
      const res = await axios.post(
        `${API_BASE}/tourists/${record.id}/update-china-doc-preview`,
        formData,
        { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        const d = res.data.data || {};
        form.setFieldsValue({
          fullName: d.fullName || '',
          documentNumber: d.documentNumber || '',
          expiryDate: d.expiryDate || '',
        });
        setWarnings(res.data.warnings || []);
        message.success(res.data.message || `${label}识别成功，请核对后提交`);
      }
    } catch (err) {
      // 识别不出不挡路：让销售手工填
      const reason = err.response?.data?.error || '识别失败';
      setOcrFailed(reason);
    } finally {
      setRecognizing(false);
    }
  };

  // ② 确认提交
  const handleSubmit = async () => {
    if (!file) { message.warning('请先选择证件照片'); return; }
    let values;
    try { values = await form.validateFields(); } catch (e) { return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('chinaDoc', file);
      formData.append('confirmData', JSON.stringify({
        fullName: values.fullName,
        documentNumber: values.documentNumber,
        expiryDate: values.expiryDate || null,
      }));
      const res = await axios.post(
        `${API_BASE}/tourists/${record.id}/update-china-doc`,
        formData,
        { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        message.success(res.data.message || `${label}上传成功`);
        setJustUploaded(res.data.data || {});   // 就地回显，不依赖父级刷新
        closeUpload();
        if (onDone) onDone();
      }
    } catch (err) {
      // 号码校验不通过、团已锁定等原因都在这里回显
      message.error(err.response?.data?.error || `${label}上传失败`);
    } finally {
      setSubmitting(false);
    }
  };

  const openUpload = () => { resetModal(); setUploadOpen(true); };

  // 两步式上传弹窗（cell / block 两种形态共用）
  const uploadModal = (
    <Modal
      title={`${doc.photo ? '重新上传' : '补传'}${label} - ${record.passportName || record.touristName || ''}`}
      open={uploadOpen}
      onCancel={closeUpload}
      width={640}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={closeUpload}>取消</Button>,
        <Button key="submit" type="primary" loading={submitting} disabled={!file || recognizing} onClick={handleSubmit}>
          确认提交
        </Button>,
      ]}
    >
      <Steps
        size="small"
        current={file ? 1 : 0}
        items={[{ title: '选择证件照片' }, { title: '核对信息并提交' }]}
        style={{ marginBottom: 20 }}
      />

      <Upload accept="image/*" showUploadList={false} beforeUpload={(f) => { handleSelect(f); return false; }}>
        <Button icon={<UploadOutlined />}>{file ? '重新选择照片' : `选择${label}照片`}</Button>
      </Upload>
      <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>
        {isIdCard ? '请拍摄身份证人像面（含姓名与公民身份号码的一面）' : '请拍摄台胞证资料页'}，四角完整、字迹清晰
      </div>

      {previewUrl && (
        <div style={{ textAlign: 'center', margin: '16px 0' }}>
          <Image src={previewUrl} alt={label} style={{ maxHeight: 220 }} />
        </div>
      )}

      {recognizing && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Spin /> <span style={{ marginLeft: 8, color: '#666' }}>正在识别{label}…</span>
        </div>
      )}

      {ocrFailed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`自动识别未成功：${ocrFailed}`}
          description="可手工填写下方的姓名与证件号码后提交，号码格式仍会校验。"
        />
      )}

      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="以下信息需人工核对"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        />
      )}

      {file && !recognizing && (
        <Form form={form} layout="vertical">
          <Form.Item
            name="fullName"
            label={`${label}姓名`}
            rules={[{ required: true, message: `请填写${label}上的姓名` }]}
            extra="身份证/台胞证上的中文姓名，与护照拼音对应"
          >
            <Input placeholder="如：张三" />
          </Form.Item>
          <Form.Item
            name="documentNumber"
            label={isIdCard ? '身份证号码' : '台胞证号码'}
            rules={[{ required: true, message: `请填写${label}号码` }]}
            extra={isIdCard ? '18 位，末位可能为 X；系统按校验位核对' : '证件正面的号码'}
          >
            <Input placeholder={isIdCard ? '18 位公民身份号码' : '台胞证号码'} />
          </Form.Item>
          {!isIdCard && (
            <Form.Item name="expiryDate" label="有效期至（选填）" extra="格式 DD/MM/YYYY">
              <Input placeholder="如：31/12/2030" />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );

  // 表格单元格：已传就只留「查看 / 重传」两个文字链（不再挂绿标，列窄时会挤到隔壁列去）
  let cell;
  if (doc.photo) {
    cell = (
      <Space size={2} style={{ whiteSpace: 'nowrap' }}>
        <Button type="link" size="small" style={{ padding: '0 4px' }}
          icon={<EyeOutlined />}
          onClick={() => (showViewer ? setViewerOpen(true) : openUpload())}>
          查看
        </Button>
        <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={openUpload}>重传</Button>
      </Space>
    );
  } else if (hasPassport(record)) {
    cell = (
      <Button type="link" size="small" style={{ padding: 0, whiteSpace: 'nowrap' }}
        icon={<UploadOutlined />} onClick={openUpload}>
        补传{label}
      </Button>
    );
  } else {
    cell = (
      <Tooltip title={`需要先上传护照，系统按护照国籍判定应交${label}`}>
        <span style={{ color: '#999' }}>待传护照</span>
      </Tooltip>
    );
  }

  // 编辑弹窗里的整块：与「护照照片」块对齐——当前照片缩略图 + 上传按钮 + 说明
  if (mode === 'block') {
    return (
      <>
        <div>
          {doc.photo ? (
            <div style={{ marginBottom: 8 }}>
              <Image
                src={photoUrl(doc.photo)}
                alt={label}
                style={{ maxWidth: 200, maxHeight: 150, border: '1px solid #d9d9d9', borderRadius: 4 }}
              />
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                当前{label}
                {doc.number ? `：${doc.name || ''} ${doc.number}` : ''}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
              {hasPassport(record) ? `尚未上传${label}` : `需先上传护照，系统按护照国籍判定应交${label}`}
            </div>
          )}
          <Button
            icon={<UploadOutlined />}
            onClick={openUpload}
            disabled={!hasPassport(record)}
          >
            {doc.photo ? `重新上传${label}` : `上传${label}`}
          </Button>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            上传后自动识别证件姓名与号码，可人工核对修改
          </div>
        </div>
        {uploadModal}
      </>
    );
  }

  return (
    <>
      {cell}

      {uploadModal}

      {showViewer && (
        <Modal
          title={`${label} - ${record.passportName || record.touristName || ''}`}
          open={viewerOpen}
          onCancel={() => setViewerOpen(false)}
          footer={null}
          width={700}
        >
          {/* 左图右信息：与护照查看弹窗同一套版式，避免上下堆叠把弹窗拉得过高 */}
          {doc.photo && (
            <Row gutter={16}>
              <Col span={14}>
                <Image src={photoUrl(doc.photo)} alt={label} style={{ width: '100%' }} />
              </Col>
              <Col span={10}>
                <div style={{ padding: '8px 0', lineHeight: 2 }}>
                  <div><strong>证件类型：</strong>{label}</div>
                  <div><strong>证件姓名：</strong>{doc.name || '-'}</div>
                  <div><strong>证件号码：</strong>{doc.number || '-'}</div>
                  <div><strong>有效期至：</strong>{doc.expiryDate ? moment(doc.expiryDate).format('YYYY-MM-DD') : '-'}</div>
                  <div><strong>上传时间：</strong>{doc.uploadedAt ? moment(doc.uploadedAt).format('YYYY-MM-DD HH:mm') : '-'}</div>
                  <div style={{ marginTop: 12 }}>
                    <Button icon={<UploadOutlined />} onClick={() => { setViewerOpen(false); openUpload(); }}>
                      重新上传
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal>
      )}
    </>
  );
}
